import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, Typography } from '@mui/material';
import { PlatformLinkingProvider, Region } from '@okletsplay/core';
import { Route } from 'app/enums';
import { TournamentEntity } from 'app/interfaces/entity';
import { sessionSelectorFactory } from 'app/store/session/selectors';
import { LinkingProviderFormat } from 'modules/platform-linking/components/Format';
import { LolPlatformLinkingProviderEntity } from 'modules/platform-linking/interfaces';
import { platformLinkingReducers } from 'modules/platform-linking/store/slice';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useStyles } from './MyTeamPlatformLinkingWarning.styles';

export const MyTeamPlatformLinkingWarning: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);
  const tournamentRegion: Region | null = useSelector(currentTournamentSelector.region);

  const platformType = useMemo(() => tournament?.game?.platformType, [tournament]);

  const connection = useSelector(sessionSelectorFactory.useConnection(platformType));

  const userRegion = useMemo(() => {
    if (connection?.platformId !== PlatformLinkingProvider.LeagueOfLegends) {
      return '';
    }

    return (connection as LolPlatformLinkingProviderEntity).region;
  }, [connection]);

  const message = useMemo(() => {
    if (!platformType) {
      return '';
    }

    if (!connection) {
      return (
        <>
          Before you can play, you&apos;ll need to link your{' '}
          <LinkingProviderFormat platformType={platformType} /> account.
        </>
      );
    }

    // Edge case for lol tournament
    return tournamentRegion !== userRegion
      ? 'The tournament is not available for use. Make sure your RIOT region is the same as in your tournament settings.'
      : '';
  }, [platformType, connection, tournamentRegion, userRegion]);

  const onClickHandler = useCallback(() => {
    if (!tournament?.id) {
      return;
    }

    dispatch(
      platformLinkingReducers.setInternalRedirect({
        pathname: Route.TournamentMyTeam.replace(':id', tournament.id),
      })
    );
    navigate(Route.PlatformLinking);
  }, [dispatch, navigate, tournament?.id]);

  if (!tournament || !message) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <ErrorIcon className={classes.icon} />
      <Typography className={classes.warningMessage}>{message}</Typography>
      <Button
        variant="contained"
        color="primary"
        className={classes.button}
        onClick={onClickHandler}
      >
        Link Account
      </Button>
    </Box>
  );
};
